export interface DadosBotanicosFCA {
  tipoIndividuo: string;
  nomePopular: string;
  nomeCientifico: string;
  familiaBotanica: string;
  origem: string;
  porte: string;
  estagioDesenvolvimento: string;
  observacoesBotanicas: string;
}

export interface FitossanidadeFCA {
  estadoGeral: string;
  condicaoCopa: string;
  condicaoTronco: string;
  condicaoRaizes: string;
  pragas: string | null;
  doencas: string | null;
  fungos: string;
  cupins: string;
  cavidades: string;
  podridao: string;
  galhosSecos: string;
  inclinacao: string;
  raizesExpostas: string;
  classeRisco: string;
  criteriosRisco: string[];
  parecerTecnico: string;
  recomendacao: string;
}

export interface VistoriaFCA {
  id: string;
  data: string;
  tipo: string;
  tecnico: string;
  estadoFito: string;
  risco: string;
  dap: number;
  altura: number;
  recomendacao: string;
  status: string;
  observacao: string;
}

export interface FotoFCA {
  id: string;
  url: string;
  tipo: string;
  data: string;
  tecnico: string;
  descricao: string;
}

export interface InterferenciaFCA {
  id: string;
  tipo: string;
  intensidade: string;
  descricao: string;
  fotoVinculada: string | null;
  recomendacao: string;
}

export interface ManejoFCA {
  recomendacaoAtual: string;
  prioridade: string;
  justificativa: string;
  status: string;
  historico: {
    id: string;
    data: string;
    tipo: string;
    status: string;
    tecnico: string;
    descricao: string;
  }[];
}

export interface DocumentoFCA {
  id: string;
  tipo: string;
  numero: string;
  titulo: string;
  data: string;
  emissor: string;
  status: string;
}

export interface EventoTimelineFCA {
  id: string;
  data: string;
  tipo: string;
  titulo: string;
  descricao: string;
  tecnico: string;
  icone: string;
}

export interface FCAData {
  id: string;
  codigoCta: string;
  nomePopular: string;
  nomeCientifico: string;
  tipo: string;
  statusRevisao: string;
  estadoFitossanitario: string;
  classeRisco: string;
  fotoPrincipal: string;
  // Quick stats
  dap: number;
  altura: number;
  diametroCopa: number;
  ultimaVistoria: string;
  fotosCount: number;
  manejoRecomendado: string;
  // Location
  municipio: string;
  uf: string;
  bairro: string;
  logradouro: string;
  numero: string;
  latitude: string;
  longitude: string;
  srid: string;
  projetoVinculado: string;
  areaVerdeVinculada: string | null;
  // Botanical
  dadosBotanicos: DadosBotanicosFCA;
  // Health & Risk
  fitossanidade: FitossanidadeFCA;
  // Inspections
  vistorias: VistoriaFCA[];
  // Photos
  fotos: FotoFCA[];
  // Interferences
  interferencias: InterferenciaFCA[];
  // Management
  manejo: ManejoFCA;
  // Documents
  documentos: DocumentoFCA[];
  // Timeline
  timeline: EventoTimelineFCA[];
  // Alerts
  alertas: { tipo: string; mensagem: string; nivel: string }[];
  // Extra for visão geral
  tecnicoResponsavel: string;
  dataCadastro: string;
}

export const fcaData: FCAData = {
  id: "ARV-001",
  codigoCta: "CTA-GO-5208707-000001",
  nomePopular: "Ipê-amarelo",
  nomeCientifico: "Handroanthus chrysotrichus (Mart. ex DC.) Mattos",
  tipo: "Árvore",
  statusRevisao: "validada",
  estadoFitossanitario: "bom",
  classeRisco: "baixo",
  fotoPrincipal: "https://readdy.ai/api/search-image?query=Adult%20golden%20trumpet%20tree%20Handroanthus%20chrysotrichus%20with%20bright%20yellow%20flowers%20in%20full%20bloom%20on%20urban%20sidewalk%2C%20green%20canopy%2C%20clear%20blue%20sky%20background%2C%20botanical%20photography%20style%2C%20sharp%20details%20on%20trunk%20texture%2C%20natural%20daylight%2C%20professional%20arboriculture%20documentation%2C%20no%20people%2C%20clean%20composition&width=800&height=600&seq=fca-ipe-amarelo-01&orientation=landscape",
  dap: 32,
  altura: 8.5,
  diametroCopa: 5.2,
  ultimaVistoria: "2026-06-25",
  fotosCount: 8,
  manejoRecomendado: "Monitorar",
  tecnicoResponsavel: "Carlos Mendes",
  dataCadastro: "2024-03-12",
  // Location
  municipio: "Goiânia",
  uf: "GO",
  bairro: "Setor Central",
  logradouro: "Av. Goiás",
  numero: "1530",
  latitude: "-16.6789",
  longitude: "-49.2645",
  srid: "EPSG:4326 (WGS84)",
  projetoVinculado: "Implantação do CTA — Goiânia Centro",
  areaVerdeVinculada: null,
  // Botanical
  dadosBotanicos: {
    tipoIndividuo: "Árvore",
    nomePopular: "Ipê-amarelo",
    nomeCientifico: "Handroanthus chrysotrichus (Mart. ex DC.) Mattos",
    familiaBotanica: "Bignoniaceae",
    origem: "Nativa",
    porte: "Médio (8-15 m)",
    estagioDesenvolvimento: "Adulto",
    observacoesBotanicas: "Indivíduo adulto em plena maturidade reprodutiva. Floração intensa entre julho e setembro com copa completamente amarela. Frutos do tipo síliqua alongada com sementes aladas. Ritidoma fissurado característico da espécie. Folhas compostas digitadas com 5 folíolos."
  },
  // Health & Risk
  fitossanidade: {
    estadoGeral: "Bom",
    condicaoCopa: "Copa bem formada, simétrica, densidade foliar acima de 80%. Ausência de galhos secos significativos. Brotação vigorosa na última estação.",
    condicaoTronco: "Tronco ereto, sem inclinação significativa (< 5°). Casca íntegra, sem cavidades ou rachaduras. Ausência de exsudação ou necroses.",
    condicaoRaizes: "Raízes não expostas. Ausência de levantamento de calçada. Colo sem sinais de estrangulamento ou podridão.",
    pragas: null,
    doencas: null,
    fungos: "Ausência de corpos de frutificação visíveis.",
    cupins: "Ausência de cupins ou galerias.",
    cavidades: "Não observadas.",
    podridao: "Ausente.",
    galhosSecos: "Pequenos ramos finos ocasionais na porção inferior da copa, dentro da normalidade.",
    inclinacao: "Menor que 5°, sem tendência de tombamento.",
    raizesExpostas: "Não observadas.",
    classeRisco: "baixo",
    criteriosRisco: [
      "Árvore adulta saudável",
      "Sistema radicular adequado",
      "Inclinação insignificante",
      "Distância segura de construções",
      "Sem histórico de queda de galhos"
    ],
    parecerTecnico: "Indivíduo em excelente estado fitossanitário. Não apresenta fatores de risco significativos. Copa bem formada com arquitetura natural equilibrada. Recomenda-se manutenção do monitoramento semestral de rotina, com atenção ao período pós-floração para verificação de galhos secos finos. Não há restrições para o entorno imediato.",
    recomendacao: "Monitorar"
  },
  // Inspections
  vistorias: [
    {
      id: "VST-001",
      data: "2026-06-25",
      tipo: "Rotina semestral",
      tecnico: "Carlos Mendes",
      estadoFito: "bom",
      risco: "baixo",
      dap: 32,
      altura: 8.5,
      recomendacao: "Monitorar",
      status: "Concluída",
      observacao: "Floração recente concluída. Copa com boa recuperação pós-floração."
    },
    {
      id: "VST-002",
      data: "2025-12-18",
      tipo: "Rotina semestral",
      tecnico: "Carlos Mendes",
      estadoFito: "bom",
      risco: "baixo",
      dap: 31,
      altura: 8.3,
      recomendacao: "Monitorar",
      status: "Concluída",
      observacao: "Pré-floração. Brotos florais abundantes. Estrutura estável."
    },
    {
      id: "VST-003",
      data: "2025-06-20",
      tipo: "Rotina semestral",
      tecnico: "Carlos Mendes",
      estadoFito: "bom",
      risco: "baixo",
      dap: 30,
      altura: 8.1,
      recomendacao: "Monitorar",
      status: "Concluída",
      observacao: "Pós-floração. Remoção de pequenos ramos secos realizada."
    },
    {
      id: "VST-004",
      data: "2024-12-15",
      tipo: "Rotina semestral",
      tecnico: "Ana Oliveira",
      estadoFito: "bom",
      risco: "baixo",
      dap: 29,
      altura: 7.9,
      recomendacao: "Monitorar",
      status: "Concluída",
      observacao: "Pré-floração. Sem alterações significativas."
    },
    {
      id: "VST-005",
      data: "2024-06-10",
      tipo: "Rotina semestral",
      tecnico: "Ana Oliveira",
      estadoFito: "bom",
      risco: "baixo",
      dap: 28,
      altura: 7.6,
      recomendacao: "Monitorar",
      status: "Concluída",
      observacao: "Pós-floração. Boa recuperação vegetativa."
    },
    {
      id: "VST-006",
      data: "2024-03-12",
      tipo: "Cadastro inicial",
      tecnico: "Carlos Mendes",
      estadoFito: "bom",
      risco: "baixo",
      dap: 28,
      altura: 7.5,
      recomendacao: "Sem intervenção",
      status: "Concluída",
      observacao: "Cadastramento inicial do indivíduo no CTA. Medições de referência."
    }
  ],
  // Photos
  fotos: [
    {
      id: "FOT-001",
      url: "https://readdy.ai/api/search-image?query=Full%20golden%20trumpet%20tree%20Handroanthus%20in%20urban%20setting%20with%20bright%20yellow%20flowers%2C%20full%20tree%20view%20from%20sidewalk%2C%20clear%20sky%2C%20professional%20arboriculture%20photo%2C%20clean%20background%2C%20sharp%20details&width=800&height=600&seq=fca-ipe-geral-01&orientation=landscape",
      tipo: "geral",
      data: "2026-06-25",
      tecnico: "Carlos Mendes",
      descricao: "Vista geral do indivíduo — pós-floração, julho/2026"
    },
    {
      id: "FOT-002",
      url: "https://readdy.ai/api/search-image?query=Close%20up%20view%20of%20tree%20canopy%20Handroanthus%20with%20dense%20green%20foliage%2C%20looking%20upward%20from%20ground%2C%20bright%20natural%20light%20filtering%20through%20leaves%2C%20professional%20botanical%20documentation%2C%20no%20flowers&width=600&height=800&seq=fca-ipe-copa-01&orientation=portrait",
      tipo: "copa",
      data: "2026-06-25",
      tecnico: "Carlos Mendes",
      descricao: "Copa — vista inferior, avaliação de densidade foliar"
    },
    {
      id: "FOT-003",
      url: "https://readdy.ai/api/search-image?query=Tree%20trunk%20close%20up%20Handroanthus%20with%20fissured%20bark%20texture%2C%20straight%20trunk%2C%20no%20damage%2C%20professional%20arboriculture%20photo%2C%20sharp%20detail%2C%20natural%20daylight%2C%20urban%20sidewalk%20background&width=600&height=800&seq=fca-ipe-tronco-01&orientation=portrait",
      tipo: "tronco",
      data: "2026-06-25",
      tecnico: "Carlos Mendes",
      descricao: "Tronco — detalhe do ritidoma fissurado, sem lesões"
    },
    {
      id: "FOT-004",
      url: "https://readdy.ai/api/search-image?query=Golden%20trumpet%20tree%20in%20full%20bloom%20with%20dense%20yellow%20flowers%20covering%20entire%20canopy%2C%20urban%20sidewalk%20setting%2C%20bright%20sunlight%2C%20professional%20botanical%20photography%2C%20spectacular%20display&width=800&height=600&seq=fca-ipe-floracao-01&orientation=landscape",
      tipo: "geral",
      data: "2025-08-20",
      tecnico: "Carlos Mendes",
      descricao: "Floração plena — agosto/2025, copa completamente amarela"
    },
    {
      id: "FOT-005",
      url: "https://readdy.ai/api/search-image?query=Tree%20base%20and%20root%20collar%20close%20up%20showing%20healthy%20root%20flare%2C%20no%20exposed%20roots%2C%20grass%20surrounding%2C%20urban%20sidewalk%2C%20professional%20arboriculture%20documentation&width=800&height=600&seq=fca-ipe-raiz-01&orientation=landscape",
      tipo: "raiz",
      data: "2026-06-25",
      tecnico: "Carlos Mendes",
      descricao: "Base e colo — sistema radicular sem exposição"
    },
    {
      id: "FOT-006",
      url: "https://readdy.ai/api/search-image?query=Urban%20sidewalk%20with%20large%20tree%20on%20avenue%2C%20showing%20distance%20to%20buildings%20and%20power%20lines%2C%20wide%20shot%2C%20professional%20urban%20forestry%20documentation%2C%20clean%20composition&width=800&height=600&seq=fca-ipe-entorno-01&orientation=landscape",
      tipo: "interferencia",
      data: "2026-06-25",
      tecnico: "Carlos Mendes",
      descricao: "Entorno — relação com calçada, via e construções"
    },
    {
      id: "FOT-007",
      url: "https://readdy.ai/api/search-image?query=Person%20measuring%20tree%20diameter%20at%20breast%20height%20with%20diameter%20tape%2C%20urban%20tree%2C%20professional%20forestry%20work%2C%20field%20documentation%2C%20natural%20light&width=800&height=600&seq=fca-ipe-dap-01&orientation=landscape",
      tipo: "geral",
      data: "2026-06-25",
      tecnico: "Carlos Mendes",
      descricao: "Medição de DAP com fita diamétrica — 32 cm"
    },
    {
      id: "FOT-008",
      url: "https://readdy.ai/api/search-image?query=Dry%20seed%20pods%20of%20Handroanthus%20tree%20on%20branches%2C%20elongated%20silique%20fruits%2C%20botanical%20close%20up%2C%20natural%20light%2C%20sharp%20detail%2C%20professional%20documentation&width=800&height=600&seq=fca-ipe-fruto-01&orientation=landscape",
      tipo: "geral",
      data: "2025-10-05",
      tecnico: "Carlos Mendes",
      descricao: "Frutos — síliquas alongadas com sementes aladas"
    }
  ],
  // Interferences
  interferencias: [
    {
      id: "INT-001",
      tipo: "Rede elétrica",
      intensidade: "Baixa",
      descricao: "Rede de baixa tensão a 4 m de distância lateral da copa. Sem contato direto com galhos.",
      fotoVinculada: "FOT-006",
      recomendacao: "Monitorar crescimento lateral. Poda de condução se necessário em 2 anos."
    },
    {
      id: "INT-002",
      tipo: "Calçada",
      intensidade: "Baixa",
      descricao: "Calçada em bom estado. Pequena ondulação superficial a 1,5 m do tronco, sem risco de tropeço.",
      fotoVinculada: "FOT-006",
      recomendacao: "Manter observação. Sem intervenção necessária no momento."
    },
    {
      id: "INT-003",
      tipo: "Via pública",
      intensidade: "Baixa",
      descricao: "Av. Goiás com 4 faixas de rolamento. Árvore a 3 m do meio-fio. Copa não invade faixa de tráfego.",
      fotoVinculada: "FOT-006",
      recomendacao: "Altura livre de galhos sobre a via adequada (> 4,5 m). Manter."
    },
    {
      id: "INT-004",
      tipo: "Acessibilidade",
      intensidade: "Baixa",
      descricao: "Piso tátil direcional a 2 m da base. Espaço livre para circulação de pedestres de 1,8 m.",
      fotoVinculada: null,
      recomendacao: "Faixa de circulação dentro da NBR 9050. Sem restrições."
    }
  ],
  // Management
  manejo: {
    recomendacaoAtual: "Monitorar",
    prioridade: "Baixa",
    justificativa: "Indivíduo saudável, sem fatores de risco significativos. O monitoramento semestral de rotina é suficiente para acompanhar a condição fitossanitária e detectar precocemente quaisquer alterações. A árvore apresenta excelente vigor vegetativo e estrutura estável.",
    status: "recomendado",
    historico: [
      {
        id: "MAN-001",
        data: "2026-06-25",
        tipo: "Monitoramento",
        status: "Executado",
        tecnico: "Carlos Mendes",
        descricao: "Vistoria de rotina semestral. Remoção de pequenos ramos finos secos na porção inferior da copa."
      },
      {
        id: "MAN-002",
        data: "2025-12-18",
        tipo: "Monitoramento",
        status: "Executado",
        tecnico: "Carlos Mendes",
        descricao: "Vistoria de rotina semestral. Avaliação pré-floração. Sem intervenções necessárias."
      },
      {
        id: "MAN-003",
        data: "2025-06-20",
        tipo: "Poda de limpeza",
        status: "Executado",
        tecnico: "Carlos Mendes",
        descricao: "Poda leve de limpeza pós-floração. Remoção de ramos epicórmicos e galhos secos finos (< 3 cm)."
      },
      {
        id: "MAN-004",
        data: "2024-12-15",
        tipo: "Monitoramento",
        status: "Executado",
        tecnico: "Ana Oliveira",
        descricao: "Vistoria de rotina semestral. Sem intervenções."
      },
      {
        id: "MAN-005",
        data: "2024-06-10",
        tipo: "Monitoramento",
        status: "Executado",
        tecnico: "Ana Oliveira",
        descricao: "Vistoria de rotina semestral pós-floração. Sem intervenções."
      }
    ]
  },
  // Documents
  documentos: [
    {
      id: "DOC-001",
      tipo: "Inventário Arbóreo",
      numero: "INV-2024-001-0012",
      titulo: "Ficha de Inventário Arbóreo — Ipê-amarelo Av. Goiás",
      data: "2024-03-12",
      emissor: "FloraIQ / Prefeitura de Goiânia",
      status: "Vigente"
    },
    {
      id: "DOC-002",
      tipo: "Laudo Fitossanitário",
      numero: "LAU-2026-025",
      titulo: "Laudo de Avaliação Fitossanitária — CTA-GO-5208707-000001",
      data: "2026-06-25",
      emissor: "Carlos Mendes — Eng. Florestal CREA-GO 12345",
      status: "Vigente"
    },
    {
      id: "DOC-003",
      tipo: "Foto Georreferenciada",
      numero: "FOT-2026-001/008",
      titulo: "Conjunto de fotos georreferenciadas da vistoria de 25/06/2026",
      data: "2026-06-25",
      emissor: "Carlos Mendes",
      status: "Anexado"
    },
    {
      id: "DOC-004",
      tipo: "Relatório de Vistoria",
      numero: "RLT-2026-042",
      titulo: "Relatório Técnico de Vistoria Semestral — junho/2026",
      data: "2026-06-25",
      emissor: "FloraIQ",
      status: "Emitido"
    },
    {
      id: "DOC-005",
      tipo: "Relatório de Vistoria",
      numero: "RLT-2025-089",
      titulo: "Relatório Técnico de Vistoria Semestral — dezembro/2025",
      data: "2025-12-18",
      emissor: "FloraIQ",
      status: "Emitido"
    },
    {
      id: "DOC-006",
      tipo: "Ordem de Serviço",
      numero: "OS-2025-012",
      titulo: "Ordem de Serviço — Poda de limpeza pós-floração",
      data: "2025-06-15",
      emissor: "Prefeitura de Goiânia — Secretaria Municipal do Meio Ambiente",
      status: "Executada"
    }
  ],
  // Timeline
  timeline: [
    {
      id: "EVT-001",
      data: "2026-06-25",
      tipo: "vistoria",
      titulo: "Vistoria semestral realizada",
      descricao: "Carlos Mendes realizou vistoria de rotina. Estado fitossanitário mantido como Bom. Pequenos ramos secos removidos. DAP atualizado para 32 cm.",
      tecnico: "Carlos Mendes",
      icone: "ri-clipboard-line"
    },
    {
      id: "EVT-002",
      data: "2026-06-25",
      tipo: "foto",
      titulo: "Novas fotos adicionadas",
      descricao: "Cinco novas fotografias georreferenciadas foram anexadas ao cadastro: vista geral, copa, tronco, base/raiz e entorno.",
      tecnico: "Carlos Mendes",
      icone: "ri-camera-line"
    },
    {
      id: "EVT-003",
      data: "2026-06-25",
      tipo: "documento",
      titulo: "Laudo fitossanitário emitido",
      descricao: "Laudo de Avaliação Fitossanitária atualizado (LAU-2026-025) foi anexado ao cadastro com parecer técnico favorável.",
      tecnico: "Carlos Mendes",
      icone: "ri-file-text-line"
    },
    {
      id: "EVT-004",
      data: "2025-12-18",
      tipo: "vistoria",
      titulo: "Vistoria semestral realizada",
      descricao: "Carlos Mendes realizou vistoria pré-floração. Brotos florais abundantes indicando boa floração para julho/2026. Estrutura estável.",
      tecnico: "Carlos Mendes",
      icone: "ri-clipboard-line"
    },
    {
      id: "EVT-005",
      data: "2025-08-20",
      tipo: "foto",
      titulo: "Registro de floração plena",
      descricao: "Fotografia documental da floração intensa com copa completamente amarela. Material anexado ao banco de imagens do CTA.",
      tecnico: "Carlos Mendes",
      icone: "ri-camera-line"
    },
    {
      id: "EVT-006",
      data: "2025-06-20",
      tipo: "manejo",
      titulo: "Poda de limpeza executada",
      descricao: "Poda leve de limpeza pós-floração executada conforme OS-2025-012. Remoção de ramos epicórmicos e galhos secos finos com diâmetro inferior a 3 cm.",
      tecnico: "Carlos Mendes",
      icone: "ri-scissors-line"
    },
    {
      id: "EVT-007",
      data: "2024-12-15",
      tipo: "vistoria",
      titulo: "Vistoria semestral realizada",
      descricao: "Ana Oliveira realizou vistoria de rotina. Sem alterações significativas. Indivíduo em bom estado.",
      tecnico: "Ana Oliveira",
      icone: "ri-clipboard-line"
    },
    {
      id: "EVT-008",
      data: "2024-03-12",
      tipo: "cadastro",
      titulo: "Cadastro inicial no CTA",
      descricao: "Indivíduo cadastrado no Cadastro Técnico Arbóreo de Goiânia por Carlos Mendes durante a campanha de Implantação do CTA — Goiânia Centro. Medições de referência registradas: DAP 28 cm, altura 7,5 m.",
      tecnico: "Carlos Mendes",
      icone: "ri-plant-line"
    }
  ],
  // Alerts
  alertas: [
    {
      tipo: "monitoramento",
      mensagem: "Próxima vistoria de rotina agendada para dezembro/2026",
      nivel: "info"
    },
    {
      tipo: "floracao",
      mensagem: "Período de floração previsto para julho/2026 — considerar poda apenas após floração",
      nivel: "info"
    }
  ]
};

export const fotosTipos = [
  { value: "todas", label: "Todas" },
  { value: "geral", label: "Geral" },
  { value: "copa", label: "Copa" },
  { value: "tronco", label: "Tronco" },
  { value: "raiz", label: "Raiz" },
  { value: "interferencia", label: "Interferência" },
  { value: "dano", label: "Dano" },
  { value: "documento", label: "Documento" }
];